import {
  useCallback, useContext, useRef, useState, type ReactNode,
} from 'react';
import { AlertTriangle, Trash2 } from 'lucide-react';
import { useDismiss } from '@/core/useDismiss';
import { Button, cn } from './ui';
import { ConfirmContext } from './confirmContext';

/* =============================================================================
 * Confirmation
 * -----------------------------------------------------------------------------
 * Asking before something cannot be taken back.
 *
 * Deliberately not window.confirm(): it cannot be styled, it is blocked
 * outright in some Android webviews, and it says "localhost says" above the
 * question, which reads as a browser fault rather than a decision the app is
 * asking for.
 *
 * A promise-returning hook rather than a component per call site, because the
 * alternative is four copies of the same dialog state and one of them
 * eventually forgetting to reset it:
 *
 *     if (await confirm({ ... })) remove.mutate(row);
 * ========================================================================== */

export interface ConfirmOptions {
  title: string;
  /** What actually happens, in particular anything that is not obvious. */
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** `danger` for anything destructive; the default for this hook. */
  tone?: 'danger' | 'default';
}

type Resolver = (confirmed: boolean) => void;

/* The context object lives in ./confirmContext, so its identity survives this
   module being re-evaluated by a hot reload. See src/core/i18nContext.ts. */


export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<Resolver | null>(null);

  const confirm = useCallback((next: ConfirmOptions) => {
    setOptions(next);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const settle = useCallback((confirmed: boolean) => {
    // Resolve before clearing, and clear the ref either way — an unresolved
    // promise here would leave the caller awaiting for ever.
    resolver.current?.(confirmed);
    resolver.current = null;
    setOptions(null);
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {options && <ConfirmDialog options={options} onSettle={settle} />}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const confirm = useContext(ConfirmContext);
  if (!confirm) throw new Error('useConfirm must be used inside <ConfirmProvider>');
  return confirm;
}

/* -------------------------------------------------------------------------- */

function ConfirmDialog({
  options, onSettle,
}: { options: ConfirmOptions; onSettle: (confirmed: boolean) => void }) {
  const danger = options.tone !== 'default';

  // Escape and an outside click both mean cancel. Cancelling is always the safe
  // answer, so the easy gestures are wired to it rather than to going ahead.
  const ref = useDismiss<HTMLDivElement>(true, () => onSettle(false));

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center px-4 pb-4 pt-safe-plus-4 sm:p-6">
      <div className="fixed inset-0 bg-black/50 backdrop-enter" aria-hidden="true" />

      <div
        ref={ref}
        role="alertdialog"
        aria-modal="true"
        aria-label={options.title}
        className={cn(
          'relative w-full sm:max-w-md bg-surface border border-line rounded-card shadow-pop',
          'p-5 sheet-enter',
        )}
      >
        <div className="flex items-start gap-3.5">
          <span
            className={cn(
              'shrink-0 w-10 h-10 rounded-card grid place-items-center',
              danger ? 'bg-danger-subtle text-danger' : 'bg-brand-subtle text-brand',
            )}
          >
            {danger ? <Trash2 size={19} /> : <AlertTriangle size={19} />}
          </span>

          <div className="min-w-0">
            <h2 className="text-[15.5px] font-bold tracking-tight">{options.title}</h2>
            {options.body && (
              <div className="text-[13px] text-muted mt-1.5 leading-relaxed">
                {options.body}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <Button variant="secondary" onClick={() => onSettle(false)}>
            {options.cancelLabel ?? 'Cancel'}
          </Button>
          <Button
            variant={danger ? 'danger' : 'primary'}
            onClick={() => onSettle(true)}
            // Focused on open so Enter confirms and Escape cancels, which is
            // what a keyboard user expects of a dialog like this.
            autoFocus
          >
            {options.confirmLabel ?? 'Delete'}
          </Button>
        </div>
      </div>
    </div>
  );
}
